import { skipToken, useQuery } from '@tanstack/react-query';

import { ShortUrl } from '@air/api';
import { type ViewTypeName } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';

import { getConfigViewOptionsKey } from '~/constants/react-query-keys';
import { filterOutInternalAnalyticsFields } from '~/swr-hooks/gallery/viewFields/filterOutInternalAnalyticsFields';
import { filterOutIPTCFields } from '~/swr-hooks/gallery/viewFields/filterOutIPTCFields';
import { useCanViewPublicAddOn } from '~/swr-hooks/workspaces/addOns/useCanViewPublicAddOn';

export type UseGetPublicConfigViewOptionsParams = {
  viewType: ViewTypeName | undefined;
};

export const useGetPublicConfigViewOptions = ({ viewType }: UseGetPublicConfigViewOptionsParams) => {
  const { shortId } = useShortIdContext();

  const { canViewAddOn: canSeePublicIPTCFields } = useCanViewPublicAddOn({ key: 'metadata' });

  return useQuery({
    queryKey: getConfigViewOptionsKey({
      viewType,
      canSeeIPTCFields: canSeePublicIPTCFields,
    }),
    queryFn:
      shortId && viewType
        ? async () => {
            let viewFields = await ShortUrl.listViewFields(shortId, viewType);

            if (!canSeePublicIPTCFields) {
              viewFields = filterOutIPTCFields(viewFields);
            }

            // Share links cannot read internal analytics, so these fields stay out of public menus.
            viewFields = filterOutInternalAnalyticsFields(viewFields);

            return viewFields;
          }
        : skipToken,
  });
};
